"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { publicEnv } from "@/config/env";
import { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { ApiSeizeSettings } from "@/generated/models/ApiSeizeSettings";
import { normalizeOptionalWaveId } from "@/helpers/waves/wave.helpers";
import { fetchUrl } from "@/services/6529api";
import { SeizeSettingsMode } from "@/types/enums";
import type { ReactNode } from "react";
type TempApiSeizeSettings = ApiSeizeSettings & {
  curation_wave_id: string | null;
};
type SeizeSettingsContextType = {
  seizeSettings: TempApiSeizeSettings;
  isMemesWave: (waveId: string | undefined | null) => boolean;
  isCurationWave: (waveId: string | undefined | null) => boolean;
  isQuorumWave: (waveId: string | undefined | null) => boolean;
  isAnnouncementsWave: (waveId: string | undefined | null) => boolean;
  isMemesSubmission: (drop: ApiDrop | undefined | null) => boolean;
  // True once at least one fetch succeeds; stays true during background refreshes
  // unless callers opt into reset=true before reloading.
  isLoaded: boolean;
  loadError: Error | null;
  loadSeizeSettings: (options?: {
    reset?: boolean | undefined;
  }) => Promise<void>;
};
const SeizeSettingsContext = createContext<SeizeSettingsContextType | undefined>(undefined);
export const SeizeSettingsProvider = t0 => {
  const $ = _c(29);
  const {
    children,
    mode: t1
  } = t0;
  const mode = t1 === undefined ? SeizeSettingsMode.REMOTE : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      rememes_submission_tdh_threshold: 0,
      all_drops_notifications_subscribers_limit: 0,
      memes_wave_id: null,
      curation_wave_id: null,
      quorum_wave_id: null,
      announcements_wave_id: null,
      distribution_admin_wallets: [],
      claims_admin_wallets: []
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [seizeSettings, setSeizeSettings] = useState(t2);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const isMountedRef = useRef(true);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = async t4 => {
      const {
        reset: t5
      } = t4 === undefined ? {} : t4;
      const reset = t5 === undefined ? false : t5;
      if (reset && isMountedRef.current) {
        setIsLoaded(false);
        setLoadError(null);
      }
      ;
      try {
        const settings = await fetchUrl(`${publicEnv.API_ENDPOINT}/api/settings`);
        if (!isMountedRef.current) {
          return;
        }
        setSeizeSettings(previous => ({
          ...previous,
          ...settings,
          distribution_admin_wallets: settings.distribution_admin_wallets ?? [],
          claims_admin_wallets: settings.claims_admin_wallets ?? [],
          memes_wave_id: publicEnv.DEV_MODE_MEMES_WAVE_ID ?? settings.memes_wave_id,
          curation_wave_id: publicEnv.DEV_MODE_CURATION_WAVE_ID ?? settings.curation_wave_id,
          quorum_wave_id: publicEnv.DEV_MODE_QUORUM_WAVE_ID ?? settings.quorum_wave_id
        }));
        setLoadError(null);
        setIsLoaded(true);
      } catch (t6) {
        const error = t6;
        if (!isMountedRef.current) {
          return;
        }
        console.error("Failed to fetch seize settings", error);
        const normalizedError = error instanceof Error ? error : new Error(String(error));
        setLoadError(normalizedError);
        if (reset) {
          setIsLoaded(false);
        }
        throw normalizedError;
      }
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const loadSeizeSettings = t3;
  let t4;
  let t5;
  if ($[2] !== mode) {
    t4 = () => {
      isMountedRef.current = true;
      if (mode === SeizeSettingsMode.LOCAL) {
        setIsLoaded(true);
        return () => {
          isMountedRef.current = false;
        };
      }
      loadSeizeSettings().catch(_temp);
      return () => {
        isMountedRef.current = false;
      };
    };
    t5 = [loadSeizeSettings, mode];
    $[2] = mode;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  const {
    memes_wave_id,
    curation_wave_id,
    quorum_wave_id,
    announcements_wave_id
  } = seizeSettings;
  let t6;
  if ($[5] !== announcements_wave_id) {
    t6 = normalizeOptionalWaveId(announcements_wave_id);
    $[5] = announcements_wave_id;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const normalizedAnnouncementsWaveId = t6;
  let t7;
  if ($[7] !== memes_wave_id) {
    t7 = waveId => {
      if (!waveId) {
        return false;
      }
      return memes_wave_id === waveId;
    };
    $[7] = memes_wave_id;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const isMemesWave = t7;
  let t8;
  if ($[9] !== curation_wave_id) {
    t8 = waveId_0 => {
      if (!waveId_0) {
        return false;
      }
      return curation_wave_id === waveId_0;
    };
    $[9] = curation_wave_id;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const isCurationWave = t8;
  let t9;
  if ($[11] !== quorum_wave_id) {
    t9 = waveId_1 => {
      if (!waveId_1) {
        return false;
      }
      return quorum_wave_id === waveId_1;
    };
    $[11] = quorum_wave_id;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const isQuorumWave = t9;
  let t10;
  if ($[13] !== normalizedAnnouncementsWaveId) {
    t10 = waveId_2 => {
      const normalizedWaveId = normalizeOptionalWaveId(waveId_2);
      if (!normalizedWaveId || !normalizedAnnouncementsWaveId) {
        return false;
      }
      return normalizedAnnouncementsWaveId === normalizedWaveId;
    };
    $[13] = normalizedAnnouncementsWaveId;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const isAnnouncementsWave = t10;
  let t11;
  if ($[15] !== isMemesWave) {
    t11 = drop => {
      if (!drop) {
        return false;
      }
      return isMemesWave(drop?.wave?.id) && (drop.drop_type === ApiDropType.Participatory || drop.drop_type === ApiDropType.Winner);
    };
    $[15] = isMemesWave;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const isMemesSubmission = t11;
  let t12;
  if ($[17] !== isAnnouncementsWave || $[18] !== isCurationWave || $[19] !== isLoaded || $[20] !== isMemesSubmission || $[21] !== isMemesWave || $[22] !== isQuorumWave || $[23] !== loadError || $[24] !== seizeSettings) {
    t12 = {
      seizeSettings,
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      isAnnouncementsWave,
      isMemesSubmission,
      isLoaded,
      loadError,
      loadSeizeSettings
    };
    $[17] = isAnnouncementsWave;
    $[18] = isCurationWave;
    $[19] = isLoaded;
    $[20] = isMemesSubmission;
    $[21] = isMemesWave;
    $[22] = isQuorumWave;
    $[23] = loadError;
    $[24] = seizeSettings;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const value = t12;
  let t13;
  if ($[26] !== children || $[27] !== value) {
    t13 = <SeizeSettingsContext.Provider value={value}>{children}</SeizeSettingsContext.Provider>;
    $[26] = children;
    $[27] = value;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
};
export const useSeizeSettings = () => {
  const context = useContext(SeizeSettingsContext);
  if (context === undefined) {
    throw new Error("useSeizeSettings must be used within a SeizeSettingsProvider");
  }
  return context;
};
export const useSeizeSettingsOptional = () => {
  const context = useContext(SeizeSettingsContext);
  return context ?? null;
};
function _temp() {}